import React, { useContext, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import PropTypes from 'prop-types';
import { Menu, Divider } from 'antd';
import Icon, {
	CalculatorOutlined,
	CrownOutlined,
	LoadingOutlined,
	MessageOutlined,
	TrophyOutlined,
	VideoCameraOutlined,
} from '@ant-design/icons';
import clsx from 'clsx';

import { AppContext } from '../../../../context';
import { ReactComponent as DashboardSvg } from '../../../../assets/icons/dashboard.svg';
import { ReactComponent as PsikologiSvg } from '../../../../assets/icons/psikologi.svg';
import { ReactComponent as BooksSvg } from '../../../../assets/icons/akademik.svg';
import { ReactComponent as PullUpSvg } from '../../../../assets/icons/run-vector.svg';
import { ReactComponent as NewsSvg } from '../../../../assets/icons/news.svg';
import { ReactComponent as LogoutSvg } from '../../../../assets/icons/logout.svg';

import './styles.scss';

const { Item } = Menu;

/**
 * Mapping statis: category key (Firestore) → label, icon, route di sidebar.
 * Tambah entry baru di sini saat ada kategori latihan baru (misal TWK).
 * Keberadaan item di sidebar dikontrol oleh Firestore (menuConfigs/{latihanType}).
 */
const CATEGORY_MENU_MAP = [
	{
		key: 'psikologi',
		label: 'Test Psikologi',
		icon: PsikologiSvg,
		route: 'psikologi',
	},
	{
		key: 'akademik',
		label: 'Test Akademik',
		icon: BooksSvg,
		route: 'akademik',
	},
	{ key: 'jasmani', label: 'Test Jasmani', icon: PullUpSvg, route: 'jasmani' },
	// Tambah kategori baru di sini, contoh:
	// { key: 'twk', label: 'TWK', icon: TwkSvg, route: 'twk' },
];

const CutomMenu = ({ collapse, onCollapse }) => {
	const location = useLocation();
	const navigate = useNavigate();
	const isTabletOrMobile = useMediaQuery({ maxWidth: 767 });
	const [loggingOut, setLoggingOut] = useState(false);

	const normalizedPath = location.pathname.replace(/^\/+/, '');
	let selectedMenuKey = normalizedPath.split('/')[0];
	if (normalizedPath.startsWith('beranda/tiket-bantuan')) {
		selectedMenuKey = 'beranda/tiket-bantuan';
	} else if (normalizedPath.startsWith('beranda/live-class')) {
		selectedMenuKey = 'beranda/live-class';
	}
	const {
		handleLogOut,
		session: { menuConfig },
	} = useContext(AppContext);

	// Tampilkan kategori yang ada di menuConfig. Fallback ke semua jika config belum dimuat.
	const availableKeys = menuConfig
		? Object.keys(menuConfig)
		: CATEGORY_MENU_MAP.map((c) => c.key);
	const categoryItems = CATEGORY_MENU_MAP.filter((c) =>
		availableKeys.includes(c.key)
	);

	const handleMenuClick = async ({ key }) => {
		if (key === 'logout') {
			if (loggingOut) return;
			setLoggingOut(true);
			await handleLogOut();
			return;
		}

		const targetPath = `/${key}`;
		const navigationEvent = new CustomEvent(
			'cadira-live-class-navigation-request',
			{
				cancelable: true,
				detail: { targetPath },
			}
		);
		window.dispatchEvent(navigationEvent);
		if (navigationEvent.defaultPrevented) return;

		navigate(targetPath, {
			replace: true,
			state: { from: location },
		});

		if (isTabletOrMobile && !collapse) {
			onCollapse(true);
		}
	};

	return (
		<Menu
			onSelect={handleMenuClick}
			className={clsx('custom-menu', { mobile: isTabletOrMobile })}
			mode="inline"
			selectedKeys={[selectedMenuKey]}
			inlineIndent={12}
		>
			<Item key="beranda" icon={<Icon component={DashboardSvg} />}>
				Beranda
			</Item>

			{!collapse && (
				<>
					<Divider orientation="left" className="menu-divider" />
					<p className="menu-devider-text">Latihan</p>
				</>
			)}

			{categoryItems.map((item) => (
				<Item key={item.route} icon={<Icon component={item.icon} />}>
					{item.label}
				</Item>
			))}

			{!collapse && (
				<>
					<Divider orientation="left" className="menu-divider" />
					<p className="menu-devider-text">Akun</p>
				</>
			)}

			<Item key="beranda/tiket-bantuan" icon={<MessageOutlined />}>
				Tiket Bantuan
			</Item>

			<Item key="beranda/live-class" icon={<VideoCameraOutlined />}>
				Live Class
			</Item>

			<Item key="paket" icon={<CrownOutlined />}>
				Paket &amp; Harga
			</Item>

			{!collapse && (
				<>
					<Divider orientation="left" className="menu-divider" />
					<p className="menu-devider-text">Informasi</p>
				</>
			)}

			<Item key="leaderboard" icon={<TrophyOutlined />}>
				Leaderboard
			</Item>

			<Item key="kalkulator" icon={<CalculatorOutlined />}>
				Kalkulator
			</Item>

			<Item key="about" icon={<Icon component={NewsSvg} />}>
				Tentang Cadira
			</Item>

			{!collapse && <Divider orientation="left" className="menu-divider" />}

			<Item
				key="logout"
				icon={
					loggingOut ? <LoadingOutlined spin /> : <Icon component={LogoutSvg} />
				}
				className="logout-item"
				disabled={loggingOut}
			>
				{loggingOut ? 'Keluar...' : 'Keluar Akun'}
			</Item>
		</Menu>
	);
};

export default CutomMenu;

CutomMenu.propTypes = {
	collapse: PropTypes.bool.isRequired,
	onCollapse: PropTypes.func.isRequired,
};
